import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed inset-0 z-50 overflow-y-auto",
  "aria-labelledby": "modal-title",
  role: "dialog",
  "aria-modal": "true"
}
const _hoisted_2 = { class: "flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" }
const _hoisted_3 = { class: "inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white" }
const _hoisted_4 = { class: "px-4 pt-5 pb-4 sm:p-6 sm:pb-4 bg-white dark:bg-gray-800" }
const _hoisted_5 = { class: "sm:flex sm:items-start" }
const _hoisted_6 = { class: "w-full" }
const _hoisted_7 = { class: "text-lg leading-6 font-medium mb-4 text-gray-900 dark:text-gray-100" }
const _hoisted_8 = { class: "mb-4" }
const _hoisted_9 = { class: "mb-4" }
const _hoisted_10 = { class: "space-y-4" }
const _hoisted_11 = { class: "flex items-center justify-between mb-1" }
const _hoisted_12 = {
  key: 0,
  class: "border rounded-md p-3 border-gray-300 dark:border-gray-600"
}
const _hoisted_13 = { class: "flex items-center justify-between mb-1" }
const _hoisted_14 = {
  key: 0,
  class: "border rounded-md p-3 border-gray-300 dark:border-gray-600"
}
const _hoisted_15 = { class: "flex items-center justify-between mb-1" }
const _hoisted_16 = {
  key: 0,
  class: "border rounded-md p-3 border-gray-300 dark:border-gray-600"
}
const _hoisted_17 = { class: "flex items-center justify-between mb-1" }
const _hoisted_18 = {
  key: 0,
  class: "border rounded-md p-3 border-gray-300 dark:border-gray-600"
}
const _hoisted_19 = { class: "px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse bg-gray-50 dark:bg-gray-700" }
const _hoisted_20 = ["disabled"]

import { ref, reactive, watch } from 'vue'
import { istioMeshApiClient } from '../../../services/api/istioMeshApiClient'
import Toast from '../../base/common/Toast.vue'
import type { RequestFilter } from '../../../types/request-filter'

interface FilterData {
  body: Record<string, unknown>
  query: Record<string, unknown>
  params: Record<string, unknown>
  headers: Record<string, unknown>
  [key: string]: Record<string, unknown> | unknown
}

interface ToastState {
  show: boolean
  message: string
  type: 'error' | 'success'
  details?: string[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateFilterModal',
  props: {
    modelValue: { type: Boolean },
    editMode: { type: Boolean },
    filterData: {}
  },
  emits: ["update:modelValue", "created", "updated"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit

const props = __props

const isSubmitting = ref(false)
const showSections = reactive({
  body: false,
  query: false,
  params: false,
  headers: false
})

const formData = reactive({
  path: '',
  version: '',
  filter: {
    body: '',
    query: '',
    params: '',
    headers: ''
  }
})

const toast = reactive<ToastState>({
  show: false,
  message: '',
  type: 'success',
  details: []
})

// Reset form when modal closes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      // Only reset if not in edit mode or if edit mode is finished
      if (!props.editMode) {
        Object.assign(formData, {
          path: '',
          version: '',
          filter: {
            body: '',
            query: '',
            params: '',
            headers: ''
          }
        })
      }
    }
  }
)

// Initialize form data when filterData changes
watch(
  () => props.filterData,
  (newFilter) => {
    if (newFilter) {
      formData.path = newFilter.path
      formData.version = newFilter.version
      formData.filter = {
        body: newFilter.filter?.body ? JSON.stringify(newFilter.filter.body, null, 2) : '',
        query: newFilter.filter?.query ? JSON.stringify(newFilter.filter.query, null, 2) : '',
        params: newFilter.filter?.params ? JSON.stringify(newFilter.filter.params, null, 2) : '',
        headers: newFilter.filter?.headers ? JSON.stringify(newFilter.filter.headers, null, 2) : ''
      }
    }
  },
  { immediate: true }
)

const toggleSection = (section: keyof typeof showSections) => {
  showSections[section] = !showSections[section]
}

const validateJSON = (value: string, field: string): string[] => {
  // Skip validation if the field is empty or only contains whitespace
  if (!value || value.trim() === '') return []
  try {
    JSON.parse(value)
    return []
  } catch (e) {
    return [`Invalid JSON in ${field} filter`]
  }
}

const showError = (message: string, details?: string[]) => {
  toast.type = 'error'
  toast.message = message
  toast.details = details
  toast.show = true
}

const showSuccess = (message: string) => {
  toast.type = 'success'
  toast.message = message
  toast.details = []
  toast.show = true
}

const handleSubmit = async () => {
  try {
    isSubmitting.value = true

    // Validate JSON in all filter fields
    const errors: string[] = []
    if (formData.filter.body?.trim()) {
      errors.push(...validateJSON(formData.filter.body, 'body'))
    }
    if (formData.filter.query?.trim()) {
      errors.push(...validateJSON(formData.filter.query, 'query'))
    }
    if (formData.filter.params?.trim()) {
      errors.push(...validateJSON(formData.filter.params, 'params'))
    }
    if (formData.filter.headers?.trim()) {
      errors.push(...validateJSON(formData.filter.headers, 'headers'))
    }

    if (errors.length > 0) {
      showError('Please fix the following errors:', errors)
      return
    }

    // Parse JSON strings to objects
    const filterData = {
      path: formData.path,
      version: formData.version,
      filter: {
        body: formData.filter.body?.trim() ? JSON.parse(formData.filter.body) : undefined,
        query: formData.filter.query?.trim() ? JSON.parse(formData.filter.query) : undefined,
        params: formData.filter.params?.trim() ? JSON.parse(formData.filter.params) : undefined,
        headers: formData.filter.headers?.trim() ? JSON.parse(formData.filter.headers) : undefined
      }
    }

    // Remove undefined filter properties
    const filter = filterData.filter as FilterData
    Object.keys(filter).forEach((key) => {
      if (filter[key] === undefined) {
        delete filter[key]
      }
    })

    try {
      if (props.editMode && props.filterData?._id) {
        // Update existing filter
        await istioMeshApiClient.put(
          `/platform/istio/request-filter/${props.filterData._id}`,
          filterData
        )
        showSuccess('Request filter updated successfully')
        emit('updated')
      } else {
        // Create new filter
        await istioMeshApiClient.post('/platform/istio/request-filter', filterData)
        showSuccess('Request filter created successfully')
        emit('created')
      }

      emit('update:modelValue', false)

      // Reset form
      Object.assign(formData, {
        path: '',
        version: '',
        filter: {
          body: '',
          query: '',
          params: '',
          headers: ''
        }
      })
    } catch (error: unknown) {
      // Handle API-specific errors
      let errorMessage = props.editMode ? 'Failed to update filter' : 'Failed to create filter'
      if (error instanceof Error) {
        const message = error.message
        if (message.includes('duplicate key error')) {
          errorMessage = 'A filter with this path already exists'
        } else if (message.includes('Error in creating Istio Request Filter')) {
          errorMessage = message.split(': ').slice(1).join(': ').trim()
        } else {
          errorMessage = message
        }
      }
      showError(errorMessage)
    }
  } catch (error) {
    // Failed to process filter - show user-friendly error instead of console.error
    showError('Invalid input', ['Please make sure all filled JSON fields are properly formatted.'])
  } finally {
    isSubmitting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_ctx.modelValue)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", {
              class: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity",
              "aria-hidden": "true",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:modelValue', false)))
            }),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("form", {
                onSubmit: _withModifiers(handleSubmit, ["prevent"])
              }, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("h3", _hoisted_7, _toDisplayString(_ctx.editMode ? 'Edit Request Filter' : 'Create Request Filter'), 1),
                      _createElementVNode("div", _hoisted_8, [
                        _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" }, "Path *", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.path) = $event)),
                          type: "text",
                          class: "w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
                          placeholder: "/api/v1/example",
                          required: ""
                        }, null, 512), [
                          [_vModelText, formData.path]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_9, [
                        _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" }, "Version *", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.version) = $event)),
                          type: "text",
                          class: "w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
                          placeholder: "v1",
                          required: ""
                        }, null, 512), [
                          [_vModelText, formData.version]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_11, [
                            _cache[15] || (_cache[15] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300" }, "Body Filters", -1)),
                            _createElementVNode("button", {
                              type: "button",
                              class: "text-sm text-blue-500 hover:text-blue-400",
                              onClick: _cache[3] || (_cache[3] = ($event: any) => (toggleSection('body')))
                            }, _toDisplayString(showSections.body ? 'Hide' : 'Show'), 1)
                          ]),
                          (showSections.body)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                _withDirectives(_createElementVNode("textarea", {
                                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.filter.body) = $event)),
                                  class: "w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
                                  placeholder: "{ \"key\": \"value\" }"
                                }, null, 512), [
                                  [_vModelText, formData.filter.body]
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_13, [
                            _cache[16] || (_cache[16] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300" }, "Query Filters", -1)),
                            _createElementVNode("button", {
                              type: "button",
                              class: "text-sm text-blue-500 hover:text-blue-400",
                              onClick: _cache[5] || (_cache[5] = ($event: any) => (toggleSection('query')))
                            }, _toDisplayString(showSections.query ? 'Hide' : 'Show'), 1)
                          ]),
                          (showSections.query)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                _withDirectives(_createElementVNode("textarea", {
                                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.filter.query) = $event)),
                                  class: "w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
                                  placeholder: "{ \"param\": \"value\" }"
                                }, null, 512), [
                                  [_vModelText, formData.filter.query]
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_15, [
                            _cache[17] || (_cache[17] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300" }, "Params Filters", -1)),
                            _createElementVNode("button", {
                              type: "button",
                              class: "text-sm text-blue-500 hover:text-blue-400",
                              onClick: _cache[7] || (_cache[7] = ($event: any) => (toggleSection('params')))
                            }, _toDisplayString(showSections.params ? 'Hide' : 'Show'), 1)
                          ]),
                          (showSections.params)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _withDirectives(_createElementVNode("textarea", {
                                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formData.filter.params) = $event)),
                                  class: "w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
                                  placeholder: "{ \"id\": \":id\" }"
                                }, null, 512), [
                                  [_vModelText, formData.filter.params]
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_17, [
                            _cache[18] || (_cache[18] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300" }, "Headers Filters", -1)),
                            _createElementVNode("button", {
                              type: "button",
                              class: "text-sm text-blue-500 hover:text-blue-400",
                              onClick: _cache[9] || (_cache[9] = ($event: any) => (toggleSection('headers')))
                            }, _toDisplayString(showSections.headers ? 'Hide' : 'Show'), 1)
                          ]),
                          (showSections.headers)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                _withDirectives(_createElementVNode("textarea", {
                                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formData.filter.headers) = $event)),
                                  class: "w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
                                  placeholder: "{ \"Authorization\": \"Bearer token\" }"
                                }, null, 512), [
                                  [_vModelText, formData.filter.headers]
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ])
                      ])
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("button", {
                    type: "submit",
                    class: "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2.5 bg-blue-500 text-base font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 sm:w-auto sm:text-sm",
                    disabled: isSubmitting.value
                  }, _toDisplayString(isSubmitting.value
                  ? _ctx.editMode
                    ? 'Updating...'
                    : 'Creating...'
                  : _ctx.editMode
                    ? 'Update Filter'
                    : 'Create Filter'), 9, _hoisted_20),
                  _createElementVNode("button", {
                    type: "button",
                    class: "mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2.5 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600",
                    onClick: _cache[11] || (_cache[11] = ($event: any) => (_ctx.$emit('update:modelValue', false)))
                  }, " Cancel ")
                ])
              ], 32)
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(Toast, {
      show: toast.show,
      "onUpdate:show": _cache[12] || (_cache[12] = ($event: any) => ((toast.show) = $event)),
      message: toast.message,
      type: toast.type,
      details: toast.details,
      duration: 5000
    }, null, 8, ["show", "message", "type", "details"])
  ], 64))
}
}

})